<template>
  <v-dialog v-model="dialog" :max-width="step === 'anomalies' ? 900 : 600" persistent scrollable>
    <v-card>
      <v-card-title>
        <h3>{{ $t('expense-refund.closure.confirm-title') }}</h3>
      </v-card-title>

      <v-divider />

      <v-card-text style="max-height: 60vh; overflow-y: auto;" class="pt-4">

        <!-- Anomalies check step -->
        <div v-if="step === 'anomalies'">
          <div v-if="anomalyLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="32" />
            <p class="mt-3 grey--text">Vérification des anomalies en cours...</p>
          </div>
          <subscription-anomaly-list
              ref="anomalyList"
              :organization-id="organizationId"
              :expense-period-id="periodId"
              @loading="anomalyLoading = $event"
              @loaded="onAnomaliesLoaded"
          />
        </div>

        <!-- Confirmation step -->
        <div v-if="step === 'confirm'" class="pt-2">
          <p>{{ $t('expense-refund.closure.confirm-message', { count: memberCount }) }}</p>
        </div>

        <!-- Progress step -->
        <div v-if="step === 'progress'" class="pt-2">
          <p class="mb-2">{{ $t('expense-refund.closure.in-progress') }}</p>
          <v-progress-linear
              :value="progressPercent"
              color="primary"
              height="20"
              striped
          >
            <template #default>
              <strong class="white--text">{{ progress.done }} / {{ progress.total }}</strong>
            </template>
          </v-progress-linear>

          <v-list dense class="mt-4">
            <v-list-item v-for="(item, index) in progress.items" :key="index" class="px-0">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="itemIconColor(item.status)">{{ itemIcon(item.status) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ itemLabel(item) }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.status === 'FAILED' && item.errorMessage">
                <v-tooltip left>
                  <template #activator="{ on }">
                    <v-icon small color="error" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>{{ item.errorMessage }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <!-- Done step -->
        <div v-if="step === 'done'">
          <v-alert type="success" :class="progress.items.length ? 'mb-2' : 'mb-0'">
            {{ $t('expense-refund.closure.done') }}
          </v-alert>

          <v-list v-if="progress.items.length" dense class="mt-2">
            <v-list-item v-for="(item, index) in progress.items" :key="index" class="px-0">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="itemIconColor(item.status)">{{ itemIcon(item.status) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ itemLabel(item) }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.status === 'FAILED' && item.errorMessage">
                <v-tooltip left>
                  <template #activator="{ on }">
                    <v-icon small color="error" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>{{ item.errorMessage }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <!-- Failed step -->
        <div v-if="step === 'failed'">
          <v-alert type="error" class="mb-2">
            {{ $t('expense-refund.closure.done-with-errors') }}
          </v-alert>
          <p>{{ $tc('expense-refund.closure.failed-count', failedCount, { count: failedCount }) }}</p>

          <v-list dense class="mt-2">
            <v-list-item v-for="(item, index) in progress.items" :key="index" class="px-0">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="itemIconColor(item.status)">{{ itemIcon(item.status) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ itemLabel(item) }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.status === 'FAILED' && item.errorMessage">
                <v-tooltip left>
                  <template #activator="{ on }">
                    <v-icon small color="error" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>{{ item.errorMessage }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />

        <!-- Anomalies step actions -->
        <template v-if="step === 'anomalies'">
          <v-btn small @click="close">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" class="ml-3" @click="step = 'confirm'" :disabled="anomalyLoading">
            {{ $t('continue') }}
          </v-btn>
        </template>

        <!-- Confirm step actions -->
        <template v-if="step === 'confirm'">
          <v-btn small @click="close">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" class="ml-3" @click="startClosure" :loading="starting">
            {{ $t('expense-refund.closure.confirm-title') }}
          </v-btn>
        </template>

        <!-- Progress step actions -->
        <template v-if="step === 'progress'">
          <v-btn small @click="close">{{ $t('close') }}</v-btn>
        </template>

        <!-- Done step actions -->
        <template v-if="step === 'done'">
          <v-btn small color="primary" @click="closeDone">{{ $t('close') }}</v-btn>
        </template>

        <!-- Failed step actions -->
        <template v-if="step === 'failed'">
          <v-btn small @click="close">{{ $t('close') }}</v-btn>
          <v-btn small color="primary" class="ml-3" @click="startClosure" :loading="starting">
            {{ $t('expense-refund.closure.retry') }}
          </v-btn>
        </template>

      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ExpenseRefundRepository from "@repository/ExpenseRefundRepository";
import SubscriptionAnomalyList from "@elements/SubscriptionAnomalyList.vue";

export default {
  components: { SubscriptionAnomalyList },

  props: {
    periodId: String,
    organizationId: String,
    memberCount: {
      type: Number,
      default: 0,
    },
    members: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      dialog: false,
      step: 'anomalies', // anomalies | confirm | progress | done | failed
      starting: false,
      anomalyLoading: true,
      anomalyCount: 0,
      pollingInterval: null,
      progress: {
        status: null,
        done: 0,
        total: 0,
        failed: 0,
        items: [],
      },
    }
  },

  computed: {
    progressPercent() {
      if (!this.progress.total) return 0
      return Math.round((this.progress.done / this.progress.total) * 100)
    },

    failedCount() {
      return this.progress.failed
    },

    membersMap() {
      const map = {}
      this.members.forEach(m => {
        map[m.id] = `${m.firstname} ${m.lastname}`
      })
      return map
    },
  },

  methods: {
    open() {
      this.step = 'anomalies'
      this.starting = false
      this.anomalyLoading = true
      this.anomalyCount = 0
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.anomalyList) {
          this.$refs.anomalyList.reset()
          this.$refs.anomalyList.loadData()
        }
      })
    },

    openWithProgress(progressData) {
      this.dialog = true
      this.applyProgress(progressData)
      if (progressData.status === 'IN_PROGRESS') {
        this.startPolling()
      }
    },

    onAnomaliesLoaded({ total }) {
      this.anomalyCount = total
      this.anomalyLoading = false
    },

    close() {
      this.stopPolling()
      this.dialog = false
    },

    closeDone() {
      this.close()
      this.$emit('closed')
    },

    startClosure() {
      this.starting = true
      ExpenseRefundRepository.closePeriod(this.periodId).then(() => {
        this.starting = false
        this.step = 'progress'
        this.startPolling()
      }).catch(e => {
        this.starting = false
        this.notifyError(e)
      })
    },

    startPolling() {
      this.step = 'progress'
      this.stopPolling()
      this.pollProgress()
      this.pollingInterval = setInterval(() => {
        this.pollProgress()
      }, 3000)
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    pollProgress() {
      ExpenseRefundRepository.closureProgress(this.periodId).then(data => {
        this.applyProgress(data)
      }).catch(() => {
        // silent fail on polling
      })
    },

    itemLabel(item) {
      if (item.type === 'organization') {
        return this.$t('expense-refund.closure.item-organization')
      }
      const name = this.membersMap[item.memberId]
      if (name) {
        return `${this.$t('expense-refund.closure.item-member')} – ${name}`
      }
      return this.$t('expense-refund.closure.item-member')
    },

    applyProgress(data) {
      const counters = data.counters || {}
      this.progress = {
        status: data.status,
        done: counters.done || 0,
        total: counters.total || 0,
        failed: counters.failed || 0,
        items: data.items || [],
      }

      if (data.status === 'DONE') {
        this.stopPolling()
        this.step = 'done'
      } else if (data.status === 'FAILED') {
        this.stopPolling()
        this.step = 'failed'
      } else if (data.status === 'IN_PROGRESS') {
        this.step = 'progress'
      }
    },

    itemIcon(status) {
      const icons = {
        DONE: 'mdi-check-circle',
        IN_PROGRESS: 'mdi-progress-clock',
        FAILED: 'mdi-alert-circle',
        SKIPPED: 'mdi-minus-circle',
        PENDING: 'mdi-clock-outline',
      }
      return icons[status] || 'mdi-clock-outline'
    },

    itemIconColor(status) {
      const colors = {
        DONE: 'success',
        IN_PROGRESS: 'primary',
        FAILED: 'error',
        SKIPPED: 'grey',
        PENDING: 'grey lighten-1',
      }
      return colors[status] || 'grey'
    },
  },

  beforeDestroy() {
    this.stopPolling()
  },
}
</script>
